import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { trackPageView } from '../lib/analytics';
import {
  Megaphone, Users, MapPin, Mail, CheckCircle, ArrowRight,
  Briefcase, Sparkles, Target, Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTEREST_OPTIONS = [
  'Homepage banner / sponsor placement',
  'Search results placement',
  'Engineer dashboard / app integration',
  'Discount / partnership offer for engineers',
  'Email newsletter sponsorship',
  'Other — let\'s talk',
];

const Partners = () => {
  const [form, setForm] = useState({
    company: '',
    contactName: '',
    email: '',
    phone: '',
    interest: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { trackPageView('partners'); }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.company.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Company, email, and message are required.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/partner-enquiry`, form);
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Please email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="partners-page" data-testid="partners-page">
        <Helmet>
          <title>Partners &amp; Sponsors | The Gas Guide</title>
        </Helmet>
        <section className="partners-hero">
          <div className="container-premium">
            <div className="partners-success">
              <div className="partners-success-icon">
                <CheckCircle size={56} />
              </div>
              <h1>Thanks — we&apos;ll be in touch</h1>
              <p>Your enquiry has been received. Dan or someone from the team will reply within 1–2 working days from <strong>admin@thegasguide.co.uk</strong>.</p>
              <Link to="/">
                <Button className="partners-back-btn" data-testid="partners-back-home">
                  Back to home <ArrowRight size={16} />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="partners-page" data-testid="partners-page">
      <Helmet>
        <title>Partners &amp; Sponsors | The Gas Guide</title>
        <meta
          name="description"
          content="Reach thousands of UK Gas Safe registered heating engineers. Sponsor, advertise, or offer member discounts via The Gas Guide."
        />
      </Helmet>

      {/* Hero */}
      <section className="partners-hero">
        <div className="container-premium">
          <span className="partners-label">
            <Megaphone size={14} /> Partners &amp; Sponsors
          </span>
          <h1 className="partners-headline">
            REACH THE UK&apos;S<br />
            <span className="text-highlight">GAS SAFE ENGINEERS</span>
          </h1>
          <p className="partners-subline">
            The Gas Guide is a fast-growing, free-to-join directory of vetted Gas Safe engineers — and the homeowners
            who are looking to hire them. If your brand serves the heating &amp; gas trade, we&apos;d love to talk.
          </p>
          <a href="#enquire" className="partners-hero-cta" data-testid="partners-hero-cta">
            Enquire about partnering <ArrowRight size={18} />
          </a>
        </div>
      </section>

      {/* Who's Listening */}
      <section className="partners-audience">
        <div className="container-premium">
          <div className="section-header-premium">
            <span className="section-label">Your Audience</span>
            <h2 className="section-title-premium">WHO YOU&apos;LL REACH</h2>
          </div>
          <div className="partners-audience-grid">
            <div className="partners-audience-card">
              <Users size={32} />
              <h3>Gas Safe Engineers</h3>
              <p>Vetted, working tradespeople actively building their businesses. They buy parts, tools, vans, software and training every single week.</p>
            </div>
            <div className="partners-audience-card">
              <Briefcase size={32} />
              <h3>Heating Businesses</h3>
              <p>From sole traders to multi-engineer companies — the people making real purchasing decisions for their fleets and teams.</p>
            </div>
            <div className="partners-audience-card">
              <MapPin size={32} />
              <h3>Homeowners Nationwide</h3>
              <p>UK homeowners actively searching by postcode for boiler installs, repairs, gas safety certificates and central heating help.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Ways to Partner */}
      <section className="partners-options">
        <div className="container-premium">
          <div className="section-header-premium">
            <span className="section-label">Ways to Partner</span>
            <h2 className="section-title-premium">HOW WE CAN WORK TOGETHER</h2>
            <p className="section-desc">Flexible placements designed for trade-facing brands, parts suppliers and service providers.</p>
          </div>
          <div className="partners-options-grid">
            <div className="partners-option-card">
              <div className="partners-option-icon">
                <Sparkles size={28} />
              </div>
              <h3>Sponsor Spots</h3>
              <p>Get your brand in front of every homeowner who lands on The Gas Guide. Homepage banners, search-results sponsor tiles and dedicated &quot;Recommended by The Gas Guide&quot; placements.</p>
            </div>
            <div className="partners-option-card featured">
              <div className="partners-option-icon">
                <Target size={28} />
              </div>
              <h3>Member Discounts &amp; Offers</h3>
              <p>Offer exclusive discounts to our verified engineers (parts, tools, training, insurance, software). We promote it in the engineer dashboard and our newsletter — you get loyal customers.</p>
            </div>
            <div className="partners-option-card">
              <div className="partners-option-icon">
                <Mail size={28} />
              </div>
              <h3>Newsletter &amp; Email</h3>
              <p>Sponsor a section of our engineer announcements or product update emails. Plain-text, native and high open-rate.</p>
            </div>
          </div>
          <p className="partners-options-note">
            Suit your needs not on this list? Custom sponsorships, content partnerships, podcast / event collaborations
            and trade-show co-marketing are all on the table. Drop us a line below.
          </p>
        </div>
      </section>

      {/* Enquiry form */}
      <section className="partners-form-section" id="enquire">
        <div className="container-premium">
          <div className="section-header-premium">
            <span className="section-label">Get in Touch</span>
            <h2 className="section-title-premium">PARTNERSHIP ENQUIRY</h2>
            <p className="section-desc">Tell us about your brand and how you&apos;d like to work with us. We&apos;ll reply within 1–2 working days.</p>
          </div>

          <form onSubmit={handleSubmit} className="partners-form" data-testid="partners-form">
            <div className="partners-form-row">
              <div className="partners-form-field">
                <label htmlFor="company">Company name *</label>
                <Input
                  id="company"
                  name="company"
                  value={form.company}
                  onChange={handleChange}
                  placeholder="e.g. Screwfix Trade"
                  required
                  data-testid="partners-form-company"
                />
              </div>
              <div className="partners-form-field">
                <label htmlFor="contactName">Your name</label>
                <Input
                  id="contactName"
                  name="contactName"
                  value={form.contactName}
                  onChange={handleChange}
                  placeholder="Jane Smith"
                  data-testid="partners-form-name"
                />
              </div>
            </div>

            <div className="partners-form-row">
              <div className="partners-form-field">
                <label htmlFor="email">Work email *</label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@company.co.uk"
                  required
                  data-testid="partners-form-email"
                />
              </div>
              <div className="partners-form-field">
                <label htmlFor="phone">Phone (optional)</label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="01234 567890"
                  data-testid="partners-form-phone"
                />
              </div>
            </div>

            <div className="partners-form-field">
              <label htmlFor="interest">I&apos;m interested in...</label>
              <select
                id="interest"
                name="interest"
                value={form.interest}
                onChange={handleChange}
                className="partners-select"
                data-testid="partners-form-interest"
              >
                <option value="">Choose one (optional)</option>
                {INTEREST_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div className="partners-form-field">
              <label htmlFor="message">Tell us more *</label>
              <textarea
                id="message"
                name="message"
                rows={6}
                value={form.message}
                onChange={handleChange}
                placeholder="Brief on what you'd like to do, budget if known, timeframe, anything we should know about your audience or goals."
                required
                className="partners-textarea"
                data-testid="partners-form-message"
              />
            </div>

            {error && (
              <p className="partners-form-error" data-testid="partners-form-error">{error}</p>
            )}

            <div className="partners-form-footer">
              <p className="partners-form-help">
                Or email us directly: <a href="mailto:admin@thegasguide.co.uk">admin@thegasguide.co.uk</a>
              </p>
              <Button
                type="submit"
                className="partners-form-submit"
                disabled={submitting}
                data-testid="partners-form-submit"
              >
                {submitting ? 'Sending…' : <>Send enquiry <Send size={16} /></>}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Partners;
